/**
 * BNPL Renewals
 *
 * Customers who have only ever paid with Klarna, Afterpay or Affirm leave
 * Manifold nothing to charge at renewal. Manifold's renewal checkout emails
 * them 7, 3 and 1 days ahead with a link to save a card or pay early, and puts
 * the link in every customer's dunning emails. This page shows how many
 * renewals depend on that, whether the emails beat the holdout, and each
 * renewal around today. Data from /manifold/bnpl-renewals
 * (services/bnplRenewals).
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts'
import { manifoldAPI } from '../services/api'
import { Card, CardContent, CardHeader, CardTitle, Button } from '../components/ui'
import { RefreshCw, Loader2, AlertCircle, ExternalLink, Info } from 'lucide-react'

const PROVIDERS = [
  { key: 'affirm', label: 'Affirm' },
  { key: 'klarna', label: 'Klarna' },
  { key: 'afterpay_clearpay', label: 'Afterpay' },
]
const providerColor = key => `var(--bnpl-${PROVIDERS.some(p => p.key === key) ? key : 'other'})`

// Plan lengths Science sells, shortest first; each keeps its color whatever
// the data holds. Anything else (7-, 13-Month, unknown) folds into Other.
const SUPPLY_SLOTS = [1, 3, 4, 6, 12]
const SUPPLIES = [
  ...SUPPLY_SLOTS.map(m => ({ key: `m${m}`, months: m, label: `${m}-Month`, color: `var(--bnpl-supply-${m})` })),
  { key: 'other', months: null, label: 'Other', color: 'var(--bnpl-other)' },
]
const supplyKeyOf = months => (SUPPLY_SLOTS.includes(Number(months)) ? `m${Number(months)}` : 'other')

// The weekly chart's two splits. Each series: { key, label, color }.
const SPLITS = {
  provider: { label: 'Provider', series: PROVIDERS.map(p => ({ ...p, color: providerColor(p.key) })) },
  supply: { label: 'Plan length', series: SUPPLIES },
}

const STATES = {
  not_yet_emailed: { label: 'Not yet emailed', tone: 'muted' },
  holdout: { label: 'Holdout (no email)', tone: 'muted' },
  emailed: { label: 'Emailed', tone: 'info' },
  opened: { label: 'Opened link', tone: 'info' },
  card_saved: { label: 'Card saved', tone: 'good' },
  paid_early_pending: { label: 'Paying early', tone: 'info' },
  renewed: { label: 'Renewed', tone: 'good' },
  renewed_card_saved: { label: 'Renewed · card saved', tone: 'good' },
  renewed_paid_early: { label: 'Renewed · paid early', tone: 'good' },
  renewed_recovered: { label: 'Renewed · recovered', tone: 'good' },
  failed: { label: 'Failed · in dunning', tone: 'bad' },
  due_unpaid: { label: 'Due · unpaid', tone: 'bad' },
}
const TONES = {
  muted: 'bg-muted text-muted-foreground',
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  good: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  bad: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
}

function money(cents) {
  if (cents == null) return '–'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(cents / 100)
}
function pct(rate) {
  if (rate == null) return '–'
  const p = rate * 100
  return p > 0 && p < 1 ? '<1%' : `${Math.round(p)}%`
}
function points(delta) {
  if (delta == null) return '–'
  const p = Math.round(delta * 100)
  return `${p > 0 ? '+' : ''}${p} pts`
}
function shortDate(value) {
  if (!value) return '–'
  const d = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function Stat({ label, value, sub }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <div className="text-muted-foreground text-xs font-medium mb-1">{label}</div>
        <div className="text-xl font-bold tabular-nums">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  )
}

function Legend({ series }) {
  return (
    <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
      {series.map(s => (
        <span key={s.key} className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
          {s.label}
        </span>
      ))}
    </div>
  )
}

function WeekTooltip({ active, payload, series }) {
  if (!active || !payload?.length) return null
  const week = payload[0].payload
  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
      <div className="font-medium mb-1">Week of {shortDate(week.week_start)}</div>
      {series.filter(s => week[s.key]).map(s => (
        <div key={s.key} className="flex items-center justify-between gap-6">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-sm" style={{ background: s.color }} />{s.label}
          </span>
          <span className="tabular-nums">{week[s.key]}</span>
        </div>
      ))}
      <div className="flex justify-between gap-6 border-t mt-1 pt-1 font-medium">
        <span>{week.renewals} renewals</span><span className="tabular-nums">{money(week.amount)}</span>
      </div>
    </div>
  )
}

/**
 * BNPL-only active subscriptions by plan length, each bar labelled with the
 * share of ALL active subscriptions of that length that are BNPL-only — the
 * count says where the volume is, the share says which plans lean on BNPL.
 */
export function SupplyCard({ bySupply }) {
  const rows = SUPPLIES.map(s => {
    const items = bySupply.filter(b => supplyKeyOf(b.months) === s.key)
    const count = items.reduce((n, b) => n + b.count, 0)
    const active = items.reduce((n, b) => n + b.active, 0)
    return { ...s, count, active, run_rate: items.reduce((n, b) => n + b.run_rate, 0), share: active ? count / active : null }
  }).filter(r => r.count > 0)
  const max = Math.max(1, ...rows.map(r => r.count))
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">BNPL-only subscriptions by plan length</CardTitle>
        <p className="text-xs text-muted-foreground">Active subscriptions whose customer has only ever paid with BNPL, and what share of that plan length they are.</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-2.5" role="list">
          {rows.map(r => (
            <div key={r.key} role="listitem" className="grid grid-cols-[4.5rem_1fr_auto] items-center gap-3 text-sm"
              title={`${r.label}: ${r.count.toLocaleString()} BNPL-only of ${r.active.toLocaleString()} active · ${money(r.run_rate)}/mo run rate`}>
              <span className="text-muted-foreground">{r.label}</span>
              <div className="h-5 rounded-r bg-muted/40">
                <div className="h-5 rounded-r" style={{ width: `${(r.count / max) * 100}%`, minWidth: 4, background: r.color }} />
              </div>
              <span className="tabular-nums text-right whitespace-nowrap">
                <span className="font-medium">{r.count.toLocaleString()}</span>
                <span className="text-muted-foreground"> · {pct(r.share)} of {r.active.toLocaleString()}</span>
              </span>
            </div>
          ))}
        </div>
        <details className="mt-3">
          <summary className="text-xs text-muted-foreground cursor-pointer select-none hover:text-foreground">As a table</summary>
          <table className="w-full text-xs mt-2">
            <thead>
              <tr className="text-muted-foreground">
                <th className="text-left py-1 font-medium">Plan</th>
                <th className="text-right py-1 font-medium">BNPL-only</th>
                <th className="text-right py-1 font-medium">All active</th>
                <th className="text-right py-1 font-medium">Share</th>
                <th className="text-right py-1 font-medium">Run rate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.key} className="border-t">
                  <td className="py-1">{r.label}</td>
                  <td className="py-1 text-right tabular-nums">{r.count.toLocaleString()}</td>
                  <td className="py-1 text-right tabular-nums">{r.active.toLocaleString()}</td>
                  <td className="py-1 text-right tabular-nums">{pct(r.share)}</td>
                  <td className="py-1 text-right tabular-nums">{money(r.run_rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      </CardContent>
    </Card>
  )
}

/** One funnel row: emailed group beside holdout. */
function FunnelRow({ label, treated, holdout, hint }) {
  return (
    <tr className="border-t">
      <td className="p-2.5">
        <div className="text-sm">{label}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </td>
      <td className="p-2.5 text-right tabular-nums">{treated}</td>
      <td className="p-2.5 text-right tabular-nums text-muted-foreground">{holdout}</td>
    </tr>
  )
}

function FunnelCard({ funnel }) {
  const t = funnel.treated
  const h = funnel.holdout
  const of = (n, d) => (d ? `${n.toLocaleString()} · ${pct(n / d)}` : n.toLocaleString())
  const empty = !t.subscriptions && !h.subscriptions
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Pre-renewal emails</CardTitle>
        <p className="text-xs text-muted-foreground">
          BNPL-only renewals from {shortDate(funnel.cycle_from)} to {shortDate(funnel.cycle_to)}. The holdout is a fixed slice that gets no email, so the gap in renewal rate is what the emails did.
        </p>
      </CardHeader>
      <CardContent>
        {empty ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No renewals recorded yet. Manifold writes one when a BNPL-only renewal is 7, 3 or 1 days out.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Emailed renewal rate</div>
                <div className="text-lg font-bold tabular-nums">{pct(t.renewal_rate)}</div>
                <div className="text-xs text-muted-foreground">{t.renewed} of {t.due} due</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Holdout renewal rate</div>
                <div className="text-lg font-bold tabular-nums">{pct(h.renewal_rate)}</div>
                <div className="text-xs text-muted-foreground">{h.renewed} of {h.due} due</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Lift</div>
                <div className="text-lg font-bold tabular-nums">{points(funnel.lift)}</div>
                <div className="text-xs text-muted-foreground">{h.due < 30 ? 'Holdout too small to trust yet' : 'Emailed minus holdout'}</div>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th className="text-left p-2.5 font-medium"></th>
                  <th className="text-right p-2.5 font-medium">Emailed</th>
                  <th className="text-right p-2.5 font-medium">Holdout</th>
                </tr>
              </thead>
              <tbody>
                <FunnelRow label="Renewals" treated={t.subscriptions} holdout={h.subscriptions} />
                <FunnelRow label="Sent at least one email" treated={of(t.emailed, t.subscriptions)} holdout="–" />
                <FunnelRow label="Opened the link" treated={of(t.opened, t.emailed)} holdout="–" />
                <FunnelRow label="Saved a card" treated={of(t.card_saved, t.emailed)} holdout="–" />
                <FunnelRow label="Paid early" treated={of(t.paid_now, t.emailed)} holdout="–" />
                <FunnelRow label="Renewal date passed" treated={t.due} holdout={h.due} />
                <FunnelRow label="Renewed" treated={of(t.renewed, t.due)} holdout={of(h.renewed, h.due)} />
                <FunnelRow label="Failed into dunning" treated={of(t.failed, t.due)} holdout={of(h.failed, h.due)} />
              </tbody>
            </table>
            {funnel.by_provider.length > 1 && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                {funnel.by_provider.map(p => (
                  <div key={p.provider} className="rounded-lg border px-3 py-2 text-xs">
                    <div className="flex items-center gap-1.5 font-medium text-sm mb-1">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: providerColor(p.provider) }} />{p.label}
                    </div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Emailed</span><span className="tabular-nums">{pct(p.treated.renewal_rate)} of {p.treated.due}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Holdout</span><span className="tabular-nums">{pct(p.holdout.renewal_rate)} of {p.holdout.due}</span></div>
                  </div>
                ))}
              </div>
            )}
            {funnel.portal_actions.total > 0 && (
              <p className="text-xs text-muted-foreground mt-3">
                Plus {funnel.portal_actions.total} fixed from the patient portal before any email ({funnel.portal_actions.card_saved} card saved, {funnel.portal_actions.paid_now} paid early, {funnel.portal_actions.recovered} recovered). Not counted above.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function RecoveryCard({ recovery }) {
  const t = recovery.treated
  const h = recovery.holdout
  const empty = !t.invoices && !h.invoices
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Failed-rebill recovery</CardTitle>
        <p className="text-xs text-muted-foreground">
          Every customer, not just BNPL: renewals that failed since {shortDate(recovery.created_from)}, where the dunning emails included the pay link or (holdout) left it out.
        </p>
      </CardHeader>
      <CardContent>
        {empty ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No failed rebills have been emailed with or without the link yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">With link</div>
                <div className="text-lg font-bold tabular-nums">{pct(t.recovery_rate)}</div>
                <div className="text-xs text-muted-foreground">{t.recovered} of {t.resolved} resolved</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Holdout</div>
                <div className="text-lg font-bold tabular-nums">{pct(h.recovery_rate)}</div>
                <div className="text-xs text-muted-foreground">{h.recovered} of {h.resolved} resolved</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Lift</div>
                <div className="text-lg font-bold tabular-nums">{points(recovery.lift)}</div>
                <div className="text-xs text-muted-foreground">{h.resolved < 30 ? 'Holdout too small to trust yet' : 'With link minus holdout'}</div>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th className="text-left p-2.5 font-medium"></th>
                  <th className="text-right p-2.5 font-medium">With link</th>
                  <th className="text-right p-2.5 font-medium">Holdout</th>
                </tr>
              </thead>
              <tbody>
                <FunnelRow label="Failed rebills" treated={t.invoices} holdout={h.invoices} />
                <FunnelRow label="Resolved" hint="Paid, or dunning ended" treated={t.resolved} holdout={h.resolved} />
                <FunnelRow label="Recovered" treated={t.recovered} holdout={h.recovered} />
                <FunnelRow label="Paid on the checkout page" hint="Rather than by a card retry" treated={t.recovered_via_checkout} holdout={h.recovered_via_checkout} />
                <FunnelRow label="Recovered revenue" treated={money(t.recovered_amount)} holdout={money(h.recovered_amount)} />
              </tbody>
            </table>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function RenewalsTable({ renewals }) {
  const [filter, setFilter] = useState('upcoming')
  const now = Date.now()
  const rows = useMemo(() => renewals.filter(r => (filter === 'upcoming' ? r.cycle >= now : r.cycle < now)), [renewals, filter, now])
  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">Renewals</CardTitle>
          <p className="text-xs text-muted-foreground">BNPL-only renewals in the next 30 days, and those Manifold tracked in the last 30.</p>
        </div>
        <div className="flex gap-1">
          {[['upcoming', 'Next 30 days'], ['recent', 'Last 30 days']].map(([key, label]) => (
            <Button key={key} size="sm" variant={filter === key ? 'default' : 'ghost'} className="h-8 text-xs" onClick={() => setFilter(key)}>{label}</Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">No renewals in this window</p>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">Renews</th>
                  <th className="text-left p-3 font-medium">Customer</th>
                  <th className="text-left p-3 font-medium">Plan</th>
                  <th className="text-right p-3 font-medium">Amount</th>
                  <th className="text-left p-3 font-medium">Paid with</th>
                  <th className="text-left p-3 font-medium">Emails</th>
                  <th className="text-left p-3 font-medium">State</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const state = STATES[r.state] || { label: r.state, tone: 'muted' }
                  return (
                    <tr key={r.id} className="border-t hover:bg-muted/30">
                      <td className="p-3 text-xs whitespace-nowrap">{shortDate(r.cycle)}</td>
                      <td className="p-3">
                        <div className="font-medium text-sm">{r.customer_name || 'Unknown'}</div>
                        {r.customer_email && <div className="text-xs text-muted-foreground">{r.customer_email}</div>}
                      </td>
                      <td className="p-3 font-mono text-xs">{r.plan || '–'}</td>
                      <td className="p-3 text-right tabular-nums">{money(r.amount)}</td>
                      <td className="p-3 text-xs whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-sm" style={{ background: providerColor(r.provider) }} />{r.provider_label}
                        </span>
                      </td>
                      <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                        {r.holdout ? 'None (holdout)' : r.reminders.length ? r.reminders.map(o => `${o}d`).join(' · ') : '–'}
                        {r.open_count > 0 && <span className="ml-1">· opened {r.open_count}×</span>}
                      </td>
                      <td className="p-3">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${TONES[state.tone]}`}>{state.label}</span>
                      </td>
                      <td className="p-3">
                        {r.hyperswitch_customer_id && (
                          <a
                            href={`https://live.hyperswitch.io/dashboard/customers/${r.hyperswitch_customer_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            <ExternalLink className="w-3 h-3" /> HS
                          </a>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default function BnplRenewalsPage() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await manifoldAPI.getBnplRenewals()
      if (response.data.success) setData(response.data.data)
    } catch (err) {
      console.error('[BNPL] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const exposure = data?.exposure
  const [split, setSplit] = useState('provider')
  const series = SPLITS[split].series
  const weeks = useMemo(() => (exposure?.weeks || []).map(w => {
    const bySupply = {}
    for (const [months, n] of Object.entries(w.by_supply || {})) {
      const key = supplyKeyOf(months)
      bySupply[key] = (bySupply[key] || 0) + n
    }
    return {
      ...w,
      label: shortDate(w.week_start),
      ...Object.fromEntries(PROVIDERS.map(p => [p.key, w.by_provider[p.key] || 0])),
      ...Object.fromEntries(SUPPLIES.map(s => [s.key, bySupply[s.key] || 0])),
    }
  }), [exposure])
  const next30 = weeks.slice(0, 4).reduce((a, w) => ({ n: a.n + w.renewals, amount: a.amount + w.amount }), { n: 0, amount: 0 })

  return (
    <div className="space-y-6 bnpl-viz">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">BNPL Renewals</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Customers who only ever paid with Klarna, Afterpay or Affirm have nothing on file to charge at renewal. Manifold emails them a link to save a card or pay early.
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive"><AlertCircle className="w-5 h-5" /><span>{error}</span></div>
          </CardContent>
        </Card>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat
              label="BNPL-only active subscriptions"
              value={exposure.bnpl_only.toLocaleString()}
              sub={`${pct(exposure.share)} of ${exposure.active.toLocaleString()} active`}
            />
            <Stat label="Their monthly run rate" value={money(exposure.run_rate)} sub="Multi-month plans spread evenly per month" />
            <Stat label="Renewing in the next 4 weeks" value={next30.n.toLocaleString()} sub={`${money(next30.amount)} due`} />
            <Stat label="Moved from BNPL to a card" value={exposure.moved_to_card.toLocaleString()} sub="Active, first paid BNPL, card on file since" />
          </div>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base">BNPL-only renewals by week</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    Scheduled renewals for the next 12 weeks, {split === 'provider' ? 'by what the customer paid with last' : 'by plan length'}.
                  </p>
                </div>
                <div className="inline-flex rounded-md border p-0.5 text-xs" role="group" aria-label="Split renewals by">
                  {Object.entries(SPLITS).map(([key, s]) => (
                    <button key={key} type="button" aria-pressed={split === key} onClick={() => setSplit(key)}
                      className={`px-2.5 py-1 rounded ${split === key ? 'bg-muted text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <Legend series={series} />
                {split === 'provider' && (
                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                    {exposure.by_provider.map(p => (
                      <span key={p.provider}>{p.label} <span className="text-foreground font-medium tabular-nums">{p.count}</span></span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weeks} margin={{ top: 4, right: 8, left: -16, bottom: 0 }} barCategoryGap="20%">
                    <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<WeekTooltip series={series} />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.5 }} />
                    {series.map((s, i) => (
                      <Bar
                        key={s.key}
                        dataKey={s.key}
                        stackId="w"
                        fill={s.color}
                        stroke="hsl(var(--card))"
                        strokeWidth={1}
                        radius={i === series.length - 1 ? [4, 4, 0, 0] : 0}
                        isAnimationActive={false}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <details className="mt-3">
                <summary className="text-xs text-muted-foreground cursor-pointer select-none hover:text-foreground">As a table</summary>
                <table className="w-full text-xs mt-2">
                  <thead>
                    <tr className="text-muted-foreground">
                      <th className="text-left py-1 font-medium">Week of</th>
                      {series.map(s => <th key={s.key} className="text-right py-1 font-medium">{s.label}</th>)}
                      <th className="text-right py-1 font-medium">Renewals</th>
                      <th className="text-right py-1 font-medium">Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeks.map(w => (
                      <tr key={w.week_start} className="border-t">
                        <td className="py-1">{w.label}</td>
                        {series.map(s => <td key={s.key} className="py-1 text-right tabular-nums">{w[s.key]}</td>)}
                        <td className="py-1 text-right tabular-nums font-medium">{w.renewals}</td>
                        <td className="py-1 text-right tabular-nums">{money(w.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
              {exposure.by_drug.length > 0 && (
                <p className="text-xs text-muted-foreground mt-3">
                  By molecule: {exposure.by_drug.map(d => `${d.drug} ${d.count}`).join(' · ')}
                </p>
              )}
            </CardContent>
          </Card>

          {exposure.by_supply?.length > 0 && <SupplyCard bySupply={exposure.by_supply} />}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <FunnelCard funnel={data.funnel} />
            <RecoveryCard recovery={data.recovery} />
          </div>

          <RenewalsTable renewals={data.renewals} />

          <p className="text-xs text-muted-foreground flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            BNPL-only uses Manifold's rule: at least one successful pay-later payment and no successful payment of any other kind. A zero-dollar card save counts as a card. A renewal counts as renewed when its cycle invoice is paid, even if it was refunded later.
          </p>
        </>
      )}
    </div>
  )
}
